/**
 *  @organization: Nanocosmos GmbH
 *
 *  Show/hide switch for the alert lifecycle markers of an alert chart ('Detection Start', 'Emitted Alert',
 *  'Alert stopped', 'Stream recovered'). They carry labels and stack vertically, which is exactly what makes
 *  them cover the data on a short alert - so they stay switchable instead of being permanent.
 *
 *  Shared by the alert charts, since every one of them draws the same markers.
 */

import React from 'react';
import { PropTypes } from 'prop-types';

import SwitchButtons from '../../../../../common/SwitchButtons/SwitchButtons';
import { DefaultBlue, DefaultGrey } from '../../../../../../constants/colors';

const SHOW = 'showAlertEvents';
const HIDE = 'hideAlertEvents';

// The lifecycle markers are the vertical annotations, the metric thresholds the horizontal ones, so the two
// groups can be told apart without either chart having to keep them in separate lists.
export function filterEventAnnotations(annotations, areEventsShown) {
   return areEventsShown ? annotations : annotations.filter((annotation) => annotation.mode !== 'vertical');
}

// Applying the filter to the finished chart options rather than to the annotation list keeps the switch
// independent of the data: it hides four markers, so rebuilding every dataset and the rolling median for it
// would be the whole series recomputed for nothing.
export function withFilteredEventAnnotations(options, areEventsShown) {
   const annotations = options?.annotation?.annotations;
   if (annotations === undefined) return options;

   return {
      ...options,
      annotation: { ...options.annotation, annotations: filterEventAnnotations(annotations, areEventsShown) },
   };
}

export function AlertChartEventsSwitch({ areEventsShown, setAreEventsShown }) {
   // currentTarget rather than target: the handler sits on the two labels, of which only the right one carries
   // a name attribute, so reading the target leaves the padding of the left button as a dead click zone.
   const handleSwitch = (event) => setAreEventsShown(event.currentTarget.id === SHOW);

   return (
      <SwitchButtons
         leftActive={areEventsShown}
         leftLabel={'Events'}
         rightLabel={'Hidden'}
         leftName={SHOW}
         rightName={HIDE}
         handleSwitch={handleSwitch}
         activeBackgroundColor={DefaultBlue()}
         inactiveBackgroundColor={DefaultGrey()}
         activeFontColor={'#FFF'}
         inactiveFontColor={'#000'}
         buttonWidth={58}
         buttonHeight={22}
         fontSize={11}
         marginLeft={10}
      />
   );
}

AlertChartEventsSwitch.propTypes = {
   areEventsShown: PropTypes.bool,
   setAreEventsShown: PropTypes.func,
};

export default AlertChartEventsSwitch;
